import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { CompositionService } from "../../../../services/CompositionService";
import { FinetuneDataset } from "../../../../model_gen/finetuning";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { 
  DatabaseIcon,
  SettingsIcon,
  FileTextIcon,
  Loader2Icon
} from "lucide-react";

interface ReviewStepProps {
  selectedDataset: string; // This is actually composition_id now
  exportPrompt: string;
  fourbit: boolean;
  baseModel: string;
  displayName: string;
  description?: string;
  promptDeletions: string[];
  warmupSteps: number;
  warmupRatio: number;
  learningRate: number;
  perDeviceTrainBatchSize: number;
  gradientAccumulationSteps: number;
  trainOnResponsesOnly: boolean;
  trainSplit: number;
  alpha?: number;
  rank?: number;
  epochs?: number;
  weightDecay?: number;
  dropout?: number;
  rslora?: boolean;
  selectedSubDataset?: FinetuneDataset | null;
}

interface Composition {
  id: number;
  name: string;
  description?: string;
  intent_group_name?: string;
  application_name?: string;
  intent_name?: string;
  base_type?: string;
  meta?: any;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  members?: Array<{
    dataset_id: number;
    dataset_name: string;
    origin_dataset_id: number;
    request_count: number;
    sampling_type: string;
    sampling_weight: number;
  }>;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  selectedDataset, // This is composition_id
  exportPrompt,
  fourbit,
  baseModel,
  displayName,
  description,
  promptDeletions,
  warmupRatio,
  learningRate,
  perDeviceTrainBatchSize,
  gradientAccumulationSteps,
  trainOnResponsesOnly,
  trainSplit,
  alpha,
  rank,
  epochs,
  weightDecay,
  dropout,
  rslora,
  selectedSubDataset,
}) => {
  const { apiKey } = usePermissions();
  const [composition, setComposition] = useState<Composition | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (selectedDataset) {
      CompositionService.getComposition(
        selectedDataset,
        setIsLoading,
        apiKey
      ).subscribe(
        (data: any) => {
          if (data) {
            setComposition(data);
          }
        },
        (error: Error) => {
          console.error("Error fetching composition details:", error);
        }
      );
    }
  }, [selectedDataset, apiKey]);

  // Helper function to get source name - matches CompositionOverview logic
  const getSourceName = (composition: Composition): React.ReactNode => {
    if (composition.base_type === "intent_group") {
      return composition.meta?.intent_name || "Intent Group";
    } else if (composition.base_type === "application_intent") {
      const appName = composition.meta?.application_name || "App";
      const intentName = composition.meta?.action_type || "Intent";
      return `${appName} / ${intentName}`;
    }
    return "Unknown";
  };

  // Helper function to get total request count from composition members
  const getTotalRequestCount = (composition: Composition): number => {
    if (!composition.members) return 0;
    return composition.members.reduce((total, member) => total + (member.request_count || 0), 0);
  };

  const renderCompositionDetails = () => {
    if (isLoading) {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-6 w-32" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-40" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-6 w-24" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-16" />
            </div>
          </div>
        </div>
      );
    }

    if (!composition) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm text-muted-foreground">No composition selected</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground font-gc-reg">Name</p>
          <p className="text-sm font-gc-reg text-foreground">{composition.name}</p>
        </div>

        <div className="space-y-1">
          <p className="text-sm text-muted-foreground font-gc-reg">Description</p>
          <p className="text-sm font-gc-reg text-foreground">{composition.description || "No description"}</p>
        </div>

        <div className="space-y-1">
          <p className="text-sm text-muted-foreground font-gc-reg">Source</p>
          <p className="text-sm font-gc-reg text-foreground">{getSourceName(composition)}</p>
        </div>

        <div className="space-y-1">
          <p className="text-sm text-muted-foreground font-gc-reg">Total Requests</p>
          <p className="text-sm font-gc-reg text-foreground">{getTotalRequestCount(composition).toLocaleString()}</p>
        </div>

        <div className="space-y-1">
          <p className="text-sm text-muted-foreground font-gc-reg">Datasets</p>
          <p className="text-sm font-gc-reg text-foreground">{composition.members?.length || 0} datasets</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Review Configuration
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Review your finetune run configuration before creating
        </p>
      </div>

      {/* Composition Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <DatabaseIcon className="w-5 h-5 text-maitai-green" />
            Composition
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderCompositionDetails()}
        </CardContent>
      </Card>

      {/* Finetune Settings Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <SettingsIcon className="w-5 h-5 text-maitai-green" />
            Finetune Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">Display Name</p>
              <p className="text-sm font-gc-reg text-foreground">{displayName || "Not specified"}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">Base Model</p>
              <p className="text-sm font-gc-reg text-foreground">{baseModel}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">4-bit Quantization</p>
              <p className="text-sm font-gc-reg text-foreground">{fourbit ? "Yes" : "No"}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">Warmup Ratio</p>
              <p className="text-sm font-gc-reg text-foreground">{warmupRatio}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">Learning Rate</p>
              <p className="text-sm font-gc-reg text-foreground">{learningRate}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">Batch Size</p>
              <p className="text-sm font-gc-reg text-foreground">{perDeviceTrainBatchSize}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">Gradient Accumulation</p>
              <p className="text-sm font-gc-reg text-foreground">{gradientAccumulationSteps}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">Train on Responses Only</p>
              <p className="text-sm font-gc-reg text-foreground">{trainOnResponsesOnly ? "Yes" : "No"}</p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground font-gc-reg">Train Split</p>
              <p className="text-sm font-gc-reg text-foreground">{(trainSplit * 100).toFixed(0)}%</p>
            </div>

            {epochs !== undefined && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-gc-reg">Epochs</p>
                <p className="text-sm font-gc-reg text-foreground">{epochs}</p>
              </div>
            )}

            {weightDecay !== undefined && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-gc-reg">Weight Decay</p>
                <p className="text-sm font-gc-reg text-foreground">{weightDecay}</p>
              </div>
            )}

            {dropout !== undefined && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-gc-reg">Dropout</p>
                <p className="text-sm font-gc-reg text-foreground">{dropout}</p>
              </div>
            )}

            {rank !== undefined && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-gc-reg">Rank</p>
                <p className="text-sm font-gc-reg text-foreground">{rank}</p>
              </div>
            )}

            {alpha !== undefined && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-gc-reg">Alpha</p>
                <p className="text-sm font-gc-reg text-foreground">{alpha}</p>
              </div>
            )}

            {rslora !== undefined && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-gc-reg">RSLoRA</p>
                <p className="text-sm font-gc-reg text-foreground">{rslora ? "Yes" : "No"}</p>
              </div>
            )}
          </div>

          {description && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-gc-reg">Description</p>
                <p className="text-sm font-gc-reg text-foreground">{description}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prompt Card */}
      {exportPrompt && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <FileTextIcon className="w-5 h-5 text-maitai-green" />
              Prompt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-4">
              <pre className="text-sm font-mono text-foreground whitespace-pre-wrap break-words">
                {exportPrompt}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReviewStep;
